import React, { useCallback, useState, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header/Header";
import WelcomeBanner from "../components/Dashboard/WelcomeBanner";
import { Button, Input, Empty, Spin, Alert } from "antd";
import { SearchOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import EmptyImg from '../images/icons/empty-folder.png';
import PageCard from "../components/Dashboard/PageCard";
import CreatePageModal from "../components/Company/Page/CreatePageModal";
import success from '../assets/animations/success.json';
import { DotLoader } from "react-spinners";
import Lottie from "lottie-react";
import { useUserPages, useCreatePage } from "../hooks/usePages";
import { toast } from "react-toastify";

const Dashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [response, setResponse] = useState(null);

  // React Query hooks - on ne passe plus searchTerm à useUserPages
  const { 
    data: pagesData, 
    isLoading, 
    error,
    refetch,
    isError
  } = useUserPages(); // Suppression du searchTerm

  const createPageMutation = useCreatePage();

  // Optimisation avec useMemo pour éviter les re-renders inutiles
  const pages = useMemo(() => pagesData?.pages || [], [pagesData?.pages]);
  
  // Filtrage côté client uniquement
  const filteredPages = useMemo(() => {
    if (!searchTerm.trim()) return pages;
    
    const searchLower = searchTerm.toLowerCase().trim();
    return pages.filter(page => 
      page.name?.toLowerCase().includes(searchLower) ||
      page.typePage?.toLowerCase().includes(searchLower) ||
      page.typeCompany?.toLowerCase().includes(searchLower)
    );
  }, [pages, searchTerm]);

  const showModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const animationEnd = useCallback(() => {
    setResponse(null);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleCreatePage = useCallback(async (pageData) => {
    try {
      await createPageMutation.mutateAsync(pageData);
      
      setResponse('success');
      setIsModalOpen(false);
      
      toast.success('Page créée avec succès!', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'light',
      });
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création de la page', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'light',
      });
    }
  }, [createPageMutation]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Loading state optimisé
  if (isLoading) {
    return (
      <div className="flex h-screen">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
          <main className="flex-1">
            <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 w-full max-w-7xl mx-auto">
              <div className="flex justify-center items-center min-h-[400px]">
                <div className="flex flex-col items-center justify-center text-center space-y-4">
                  <DotLoader size={60} color="#6076E7" />
                  <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
                    Chargement de vos pages...
                  </p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Error state amélioré
  if (isError) {
    return (
      <div className="flex h-screen">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
          <main className="flex-1">
            <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 w-full max-w-7xl mx-auto">
              <div className="text-center py-8 sm:py-12">
                <Alert
                  message="Erreur lors du chargement"
                  description={error?.message || 'Une erreur est survenue lors du chargement de vos pages.'}
                  type="error"
                  showIcon
                  action={
                    <Button 
                      size="small" 
                      danger 
                      icon={<ReloadOutlined />}
                      onClick={handleRetry}
                    >
                      Réessayer
                    </Button>
                  }
                  className="max-w-md mx-auto"
                />
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Content area */}
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        {/* Site header */}
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="flex-1">
          <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 w-full max-w-7xl mx-auto">
            {/* Welcome banner */}
            <div className="mb-6 sm:mb-8">
              <WelcomeBanner />
            </div>

            {/* Search and create section */}
            <div className="mb-6 sm:mb-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div className="flex-1 max-w-sm sm:max-w-md lg:max-w-lg relative">
                  <Input
                    placeholder="Rechercher une page..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    allowClear
                    size="large"
                    prefix={<SearchOutlined className="text-gray-400" />}
                    className="w-full"
                  />
                </div>
                <Button
                  type="primary"
                  size="large"
                  onClick={showModal}
                  icon={<PlusOutlined />}
                  className="w-full sm:w-auto sm:min-w-[160px]"
                  loading={createPageMutation.isPending}
                >
                  Créer une page
                </Button>
              </div>
            </div>

            {/* Pages grid */}
            {filteredPages.length === 0 ? (
              <div className="flex justify-center items-center min-h-[400px]">
                <div className="flex flex-col items-center justify-center text-center space-y-6">
                  <img
                    src={EmptyImg}
                    alt="Aucune page"
                    className="w-24 h-24 sm:w-32 sm:h-32 opacity-50"
                  />
                  <div className="space-y-4">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-600 dark:text-gray-400">
                      {searchTerm ? 'Aucune page trouvée' : 'Aucune page créée'}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-500 max-w-md mx-auto">
                      {searchTerm 
                        ? 'Essayez de modifier vos critères de recherche'
                        : 'Commencez par créer votre première page'
                      }
                    </p>
                    {!searchTerm && (
                      <Button 
                        type="primary" 
                        size="large" 
                        onClick={showModal}
                        icon={<PlusOutlined />}
                      >
                        Créer ma première page
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Results count */}
                <div className="mb-4 sm:mb-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {filteredPages.length} page{filteredPages.length > 1 ? 's' : ''} trouvée{filteredPages.length > 1 ? 's' : ''}
                    {searchTerm && ` pour "${searchTerm}"`}
                    {searchTerm && pages.length !== filteredPages.length && (
                      <span className="text-gray-500">
                        {' '}sur {pages.length} au total
                      </span>
                    )}
                  </p>
                </div>

                {/* Grid responsive avec Tailwind uniquement */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6">
                  {filteredPages.map((page) => (
                    <PageCard key={page._id} page={page} />
                  ))}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Create Page Modal */}
      <CreatePageModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        onSubmit={handleCreatePage}
        isLoading={createPageMutation.isPending}
      />

      {/* Success Animation */}
      {response === 'success' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-32 h-32">
            <Lottie
              animationData={success}
              loop={false}
              onComplete={animationEnd}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;